import React from "react";
import { TableRow, TableCell } from "@mui/material";
import { useTranslation } from "react-i18next";
import useDataTableStyles from "./DataTable.styles";
import { DENSITY_STYLES } from "./DataTable.theme";

export interface TableEmptyRowProps {
  /** Number of columns to span */
  colSpan: number;
  /** Custom empty message */
  message?: React.ReactNode;
  /** Density of the table row */
  density?: "compact" | "standard" | "comfortable";
  /**
   * Width of the scroll container's visible area, in px. When the columns
   * are wider than the screen, the message is centred in what is visible
   * rather than across the whole scrollable row.
   */
  visibleWidth?: number;
}

/**
 * Standard empty state row for data tables.
 * Shows a centered message when no data is available.
 */
export const TableEmptyRow: React.FC<TableEmptyRowProps> = React.memo(
  TableEmptyRowInternal,
);

function TableEmptyRowInternal({
  colSpan,
  message,
  density = "standard",
  visibleWidth,
}: TableEmptyRowProps) {
  const { t } = useTranslation();
  const classes = useDataTableStyles();

  // Density styles (from centralized theme)
  const densityPadding = DENSITY_STYLES[density].padding;

  return (
    <TableRow className={classes.tableRow}>
      <TableCell
        colSpan={colSpan}
        align="center"
        className={classes.tableCell}
        style={{
          padding: densityPadding,
          ...(visibleWidth && { paddingLeft: 0, paddingRight: 0 }),
          color: "var(--mela-text-muted)",
          fontStyle: "italic",
        }}
      >
        <div
          style={
            visibleWidth
              ? {
                  position: "sticky",
                  left: 0,
                  width: visibleWidth,
                  maxWidth: "100%",
                  padding: "0 16px",
                  boxSizing: "border-box",
                }
              : undefined
          }
        >
          {message || t("finance.common.noData", "Nessun dato disponibile")}
        </div>
      </TableCell>
    </TableRow>
  );
}
